import Encaisser from './Encaisser';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './ProspectManagement.css';
import { BarChart3, Download, FolderOpen, RefreshCw, Eye, Pencil, Trash2, Plus, Copy, SquarePlus, Phone, ThumbsUp, ThumbsDown, XCircle, Calendar, Briefcase, CheckCircle, PhoneOff, Ban, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Search, Lightbulb, Upload, AlertTriangle, ClipboardList, User, MapPin, Building2, FileText } from 'lucide-react';

function ProspectManagement({ userRole = 'admin', type = 'standard' }) {
  const [prospects, setProspects] = useState([]);
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewingProspect, setViewingProspect] = useState(null);
  const [editingProspect, setEditingProspect] = useState(null);
  const [importing, setImporting] = useState(false);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterNaf, setFilterNaf] = useState('');
  const [filterEffectif, setFilterEffectif] = useState('tous');
  const [filterEmail, setFilterEmail] = useState('tous');
  const [filterTelephone, setFilterTelephone] = useState('tous');
  const [filterCategorie, setFilterCategorie] = useState('tous');
  const [converting, setConverting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProspects, setTotalProspects] = useState(0);
  const [itemsPerPage] = useState(100);
  const [filterAgent, setFilterAgent] = useState('tous');
  const [agents, setAgents] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignData, setReassignData] = useState({
    source_agent_id: 'tous',
    target_agent_id: '',
    statut: 'tous',
    source: 'tous'
  });
  const [extractionStats, setExtractionStats] = useState(null);

  // Déterminer le endpoint API selon le type
  const apiEndpoint = type === 'extraction' ? '/api/prospects-extraction' : '/api/prospects';
  const pageTitle = type === 'extraction' ? 'Clients Potentiels Extraction' : 'Clients Potentiels';

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    adresse: '',
    notes: '',
    statut: 'nouveau',
    source: ''
  });

  // Charger les prospects quand la page ou les filtres changent
  useEffect(() => {
    fetchProspects(currentPage);
  }, [currentPage, itemsPerPage, type, filterCategorie, filterStatut, filterEmail, filterTelephone, filterAgent]);

  // Recherche avec debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProspects(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Mettre à jour filteredProspects quand prospects change
  useEffect(() => {
    setFilteredProspects(Array.isArray(prospects) ? prospects : []);
  }, [prospects]);

  // Charger les stats et agents pour l'admin sur la page extraction
  useEffect(() => {
    if (type === 'extraction' && userRole === 'admin') {
      fetchExtractionStats();
    }
  }, [type, userRole]);

  const fetchExtractionStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/prospects-extraction/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExtractionStats(response.data);
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchProspects = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Construire l'URL avec les filtres
      let url = `${apiEndpoint}?page=${page}&limit=${itemsPerPage}`;
      if (filterNaf && filterNaf.trim() !== '') {
        url += `&naf=${encodeURIComponent(filterNaf.trim())}`;
      }
      if (searchTerm && searchTerm.trim() !== '') {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (filterStatut !== 'tous') {
        url += `&statut=${encodeURIComponent(filterStatut)}`;
      }
      if (filterEffectif !== 'tous') {
        url += `&effectif=${encodeURIComponent(filterEffectif)}`;
      }
      if (filterEmail !== 'tous') {
        url += `&email=${encodeURIComponent(filterEmail)}`;
      }
      if (filterTelephone !== 'tous') {
        url += `&telephone=${encodeURIComponent(filterTelephone)}`;
      }
      if (filterCategorie !== 'tous') {
        url += `&source=${encodeURIComponent(filterCategorie)}`;
      }
      if (filterAgent !== 'tous' && type === 'extraction' && userRole === 'admin') {
        url += `&agent_id=${encodeURIComponent(filterAgent)}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Gestion du nouveau format avec pagination
      if (response.data && response.data.prospects) {
        setProspects(response.data.prospects || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalProspects(response.data.pagination?.total || 0);
        setCurrentPage(response.data.pagination?.page || 1);
      } else {
        // Fallback pour l'ancien format (tableau direct)
        setProspects(Array.isArray(response.data) ? response.data : []);
        setTotalPages(1);
        setTotalProspects(Array.isArray(response.data) ? response.data.length : 0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des prospects:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      setProspects([]);
      setTotalPages(1);
      setTotalProspects(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProspect = () => {
    setEditingProspect(null);
    setFormData({
      nom: '',
      email: '',
      telephone: '',
      entreprise: '',
      adresse: '',
      notes: '',
      statut: 'nouveau',
      source: ''
    });
    setShowModal(true);
  };

  const handleEditProspect = (prospect) => {
    setEditingProspect(prospect);
    setFormData({
      nom: prospect.nom || '',
      email: prospect.email || '',
      telephone: prospect.telephone || '',
      entreprise: prospect.entreprise || '',
      adresse: prospect.adresse || '',
      notes: prospect.notes || '',
      statut: prospect.statut || 'nouveau',
      source: prospect.source || ''
    });
    setShowModal(true);
  };

  const handleDeleteProspect = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce prospect ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiEndpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProspects();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression du prospect');
    }
  };

  // Réassigner des prospects (admin seulement)
  const handleReassign = async () => {
    if (!reassignData.target_agent_id) {
      alert('Veuillez sélectionner un agent cible');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir réassigner ces prospects ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/prospects-extraction/reassign', reassignData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message);
      setShowReassignModal(false);
      setReassignData({
        source_agent_id: 'tous',
        target_agent_id: '',
        statut: 'tous',
        source: 'tous'
      });
      fetchProspects();
      fetchExtractionStats();
    } catch (error) {
      console.error('Erreur lors de la réassignation:', error);
      alert(error.response?.data?.error || 'Erreur lors de la réassignation');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      alert('Le nom est requis');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (editingProspect) {
        // Modification
        await axios.put(`${apiEndpoint}/${editingProspect.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Création
        await axios.post(apiEndpoint, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      fetchProspects();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde du prospect');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Veuillez sélectionner un fichier CSV');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImporting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiEndpoint}/import-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const message = [
        'Import terminé!',
        `✓ ${response.data.imported} prospects importés`,
        response.data.duplicates > 0 ? `⚠️ ${response.data.duplicates} doublons ignorés` : '',
        response.data.errors > 0 ? `✗ ${response.data.errors} erreurs` : ''
      ].filter(line => line).join('\n');

      alert(message);
      fetchProspects();
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'import du fichier CSV');
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleViewDetail = (prospect) => {
    setViewingProspect(prospect);
    setShowDetailModal(true);
  };

  const handleConvertToClient = async (prospect) => {
    if (!window.confirm(`Voulez-vous convertir "${prospect.nom}" en client ?\n\nLes informations du prospect seront utilisées pour créer un nouveau client.`)) {
      return;
    }

    try {
      setConverting(true);
      const token = localStorage.getItem('token');

      // Créer un nouveau client avec les données du prospect
      const clientData = {
        nom: prospect.nom,
        email: prospect.email || '',
        telephone: prospect.telephone || '',
        entreprise: prospect.entreprise || '',
        adresse: prospect.adresse || '',
        notes: `[Converti depuis prospect]\n${prospect.notes || ''}`,
        numero_siret: '',
        offer: 'creation', // Valeur par défaut
        montant_bc: 0,
        paye: 0,
        signe: 0
      };

      await axios.post('/api/clients', clientData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mettre à jour le statut du prospect à "converti"
      await axios.put(`${apiEndpoint}/${prospect.id}`, {
        ...prospect,
        statut: 'converti'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ Le prospect "${prospect.nom}" a été converti en client avec succès !`);
      fetchProspects();
    } catch (error) {
      console.error('Erreur lors de la conversion:', error);
      alert(error.response?.data?.error || 'Erreur lors de la conversion en client');
    } finally {
      setConverting(false);
    }
  };

  const handleQuickStatusChange = async (prospect, e) => {
    e.stopPropagation();
    const newStatut = e.target.value;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${apiEndpoint}/${prospect.id}`, {
        ...prospect,
        statut: newStatut
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchProspects();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleExportResults = async () => {
    if (totalProspects === 0) {
      alert('Aucun résultat à exporter');
      return;
    }

    try {
      // Récupérer TOUS les prospects filtrés depuis le serveur
      const token = localStorage.getItem('token');
      let url = `${apiEndpoint}?page=1&limit=${totalProspects}`;
      if (filterNaf && filterNaf.trim() !== '') {
        url += `&naf=${encodeURIComponent(filterNaf.trim())}`;
      }
      if (searchTerm && searchTerm.trim() !== '') {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (filterStatut !== 'tous') {
        url += `&statut=${encodeURIComponent(filterStatut)}`;
      }
      if (filterEffectif !== 'tous') {
        url += `&effectif=${encodeURIComponent(filterEffectif)}`;
      }
      if (filterEmail !== 'tous') {
        url += `&email=${encodeURIComponent(filterEmail)}`;
      }
      if (filterTelephone !== 'tous') {
        url += `&telephone=${encodeURIComponent(filterTelephone)}`;
      }
      if (filterCategorie !== 'tous') {
        url += `&source=${encodeURIComponent(filterCategorie)}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allProspects = response.data.prospects || [];

      if (allProspects.length === 0) {
        alert('Aucun résultat à exporter');
        return;
      }

      // Créer le contenu CSV
      const headers = [
        'Nom', 'Email', 'Téléphone', 'Entreprise', 'Adresse', 'Code Postal', 'Ville',
        'SIREN', 'SIRET', 'NAF', 'Chiffre d\'affaires', 'Effectif', 'Forme Juridique',
        'Date Création', 'Statut', 'Source', 'Notes', 'Date Ajout'
      ];

      const csvRows = [headers.join(',')];

      allProspects.forEach(prospect => {
        const row = [
          prospect.nom || '',
          prospect.email || '',
          prospect.telephone || '',
          prospect.entreprise || '',
          prospect.adresse || '',
          prospect.code_postal || '',
          prospect.ville || '',
          prospect.siren || '',
          prospect.siret || '',
          prospect.naf || '',
          prospect.chiffre_affaires || '',
          prospect.effectif || '',
          prospect.forme_juridique || '',
          prospect.date_creation || '',
          prospect.statut || '',
          prospect.source || '',
          `"${(prospect.notes || '').replace(/"/g, '""')}"`,
          prospect.created_at ? new Date(prospect.created_at).toLocaleDateString('fr-FR') : ''
        ];
        csvRows.push(row.join(','));
      });

      // Créer le fichier et le télécharger
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url2 = URL.createObjectURL(blob);

      const filename = `prospects_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('href', url2);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`✅ ${allProspects.length} prospect(s) exporté(s) avec succès !`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export des prospects');
    }
  };

  const getStatutBadgeClass = (statut) => {
    switch (statut) {
      case 'nouveau': return 'statut-nouveau';
      case 'contacte': return 'statut-contacte';
      case 'interesse': return 'statut-interesse';
      case 'non_interesse': return 'statut-non-interesse';
      case 'faux_numero': return 'statut-faux-numero';
      case 'rdv_pris': return 'statut-rdv-pris';
      case 'en_negociation': return 'statut-en-negociation';
      case 'converti': return 'statut-converti';
      case 'nrp': return 'statut-nrp';
      case 'gerant_absent': return 'statut-gerant-absent';
      default: return 'statut-nouveau';
    }
  };

  const getStatutLabel = (statut) => {
    const iconStyle = {display:'inline',verticalAlign:'middle',marginRight:'4px'};
    switch (statut) {
      case 'nouveau': return <><SquarePlus size={14} style={iconStyle} /> Nouveau</>;
      case 'contacte': return <><Phone size={14} style={iconStyle} /> Contacté</>;
      case 'interesse': return <><ThumbsUp size={14} style={iconStyle} /> Intéressé</>;
      case 'non_interesse': return <><ThumbsDown size={14} style={iconStyle} /> Pas intéressé</>;
      case 'faux_numero': return <><XCircle size={14} style={iconStyle} /> Faux numéro</>;
      case 'rdv_pris': return <><Calendar size={14} style={iconStyle} /> RDV pris</>;
      case 'en_negociation': return <><Briefcase size={14} style={iconStyle} /> En négociation</>;
      case 'converti': return <><CheckCircle size={14} style={iconStyle} /> Converti</>;
      case 'nrp': return <><PhoneOff size={14} style={iconStyle} /> NRP</>;
      case 'gerant_absent': return <><Ban size={14} style={iconStyle} /> Gérant pas là</>;
      default: return statut;
    }
  };

  if (loading) {
    return (
      <div className="prospects-container">
        <p style={{ textAlign: 'center', color: '#666' }}>Chargement des prospects...</p>
      </div>
    );
  }

  return (
    <div className="prospects-container">
      <div className="prospects-header">
        <div className="prospects-header-top">
          <div>
            <h2 className="prospects-title">{pageTitle}</h2>
            <div className="prospects-count">
              {filteredProspects.length === prospects.length ? (
                <span>{totalProspects} prospect{totalProspects > 1 ? 's' : ''} total{totalProspects > 1 ? 'aux' : ''} • Page {currentPage}/{totalPages}</span>
              ) : (
                <span><strong>{filteredProspects.length}</strong> résultat{filteredProspects.length > 1 ? 's' : ''} sur {prospects.length} prospect{prospects.length > 1 ? 's' : ''} (Page {currentPage}/{totalPages})</span>
              )}
            </div>
          </div>
          <div className="prospects-actions">
            {userRole === 'admin' && (
              <>
                <button className="btn-export" onClick={handleExportResults} disabled={totalProspects === 0} title="Exporter tous les résultats filtrés">
                  <BarChart3 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Exporter ({totalProspects.toLocaleString('fr-FR')})
                </button>
                <a
                  href="/modele-prospects.csv"
                  download="modele-prospects.csv"
                  className="btn-download-model"
                >
                  <Download size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Modèle CSV
                </a>
                <label className="btn-import" htmlFor="csv-upload">
                  {importing ? 'Import...' : <><FolderOpen size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Importer CSV</>}
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    disabled={importing}
                    style={{ display: 'none' }}
                  />
                </label>
              </>
            )}
            <button className="btn-add-prospect" onClick={handleAddProspect}>
              + Ajouter
            </button>
          </div>
        </div>

        <div className="prospects-filters">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher (nom, email, entreprise, téléphone)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {type === 'standard' && (
            <div className="filter-naf">
              <label>Code NAF:</label>
              <input
                type="text"
                className="naf-input"
                placeholder="Ex: 1071C, 9602A..."
                value={filterNaf}
                onChange={(e) => setFilterNaf(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(1);
                    fetchProspects(1);
                  }
                }}
              />
              <button
                onClick={() => {
                  setCurrentPage(1);
                  fetchProspects(1);
                }}
                style={{
                  marginLeft: '5px',
                  padding: '8px 12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                Appliquer
              </button>
            </div>
          )}

          <div className="filter-statut">
            <label>Statut:</label>
            <select value={filterStatut} onChange={(e) => { setFilterStatut(e.target.value); setCurrentPage(1); }}>
              <option value="tous">Tous</option>
              <option value="nouveau">🆕 Nouveau</option>
              <option value="contacte">📞 Contacté</option>
              <option value="interesse">👍 Intéressé</option>
              <option value="non_interesse">👎 Pas intéressé</option>
              <option value="faux_numero">❌ Faux numéro</option>
              <option value="rdv_pris">📅 RDV pris</option>
              <option value="en_negociation">💼 En négociation</option>
              <option value="converti">✅ Converti</option>
              {type === 'extraction' && (
                <>
                  <option value="nrp">📵 NRP</option>
                  <option value="gerant_absent">🚫 Gérant pas là</option>
                </>
              )}
            </select>
          </div>

          {type === 'standard' && (
            <div className="filter-effectif">
              <label>Effectif:</label>
              <select value={filterEffectif} onChange={(e) => setFilterEffectif(e.target.value)}>
                <option value="tous">Tous</option>
                <option value="1-5">👥 1-5 salariés</option>
                <option value="6-10">👥 6-10 salariés</option>
                <option value="11-50">👥 11-50 salariés</option>
                <option value="51-200">👥 51-200 salariés</option>
                <option value="201+">👥 201+ salariés</option>
              </select>
            </div>
          )}

          <div className="filter-email">
            <label>Email:</label>
            <select value={filterEmail} onChange={(e) => { setFilterEmail(e.target.value); setCurrentPage(1); }}>
              <option value="tous">Tous</option>
              <option value="avec">📧 Avec email</option>
              <option value="sans">❌ Sans email</option>
            </select>
          </div>

          <div className="filter-telephone">
            <label>Téléphone:</label>
            <select value={filterTelephone} onChange={(e) => { setFilterTelephone(e.target.value); setCurrentPage(1); }}>
              <option value="tous">Tous</option>
              <option value="avec">📞 Avec téléphone</option>
              <option value="sans">❌ Sans téléphone</option>
            </select>
          </div>

          <div className="filter-categorie">
            <label>Catégorie:</label>
            <select
              value={filterCategorie}
              onChange={(e) => {
                setFilterCategorie(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="tous">Toutes</option>
              {type === 'extraction' ? (
                <>
                  <option value="Restaurants">🍽️ Restaurants</option>
                  <option value="Coiffeurs">💇 Coiffeurs</option>
                  <option value="Fleuristes Paris">💐 Fleuristes Paris</option>
                  <option value="Cavistes Paris">🍷 Cavistes Paris</option>
                  <option value="Bars Paris">🍸 Bars Paris</option>
                  <option value="Boulangeries Paris">🥖 Boulangeries Paris</option>
                  <option value="Créateurs Paris">🎨 Créateurs Paris</option>
                  <option value="Beauté Paris">💅 Beauté Paris</option>
                  <option value="Pharmacies Paris">💊 Pharmacies Paris</option>
                  <option value="Immobilier Paris">🏠 Immobilier Paris</option>
                  <option value="Épiceries Paris">🛒 Épiceries Paris</option>
                  <option value="Boutiques Paris">👗 Boutiques Paris</option>
                </>
              ) : (
                <>
                  <option value="Restaurant Paris">🍽️ Restaurant Paris</option>
                  <option value="Restaurant 92">🍽️ Restaurant 92</option>
                  <option value="Restaurant 94">🍽️ Restaurant 94</option>
                  <option value="Boulangeries">🥖 Boulangeries</option>
                  <option value="Coiffure Paris">💇 Coiffure Paris</option>
                  <option value="Coiffure 92">💇 Coiffure 92</option>
                  <option value="Coiffure 94">💇 Coiffure 94</option>
                  <option value="Institut Beauté Paris">💅 Institut Beauté Paris</option>
                  <option value="Institut Beauté 92">💅 Institut Beauté 92</option>
                </>
              )}
            </select>
          </div>

          {/* Filtre par agent (admin seulement sur extraction) */}
          {type === 'extraction' && userRole === 'admin' && (
            <div className="filter-agent">
              <label>Agent:</label>
              <select
                value={filterAgent}
                onChange={(e) => {
                  setFilterAgent(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="tous">Tous les agents</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>👤 {agent.nom}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Panel de réassignation et stats (admin seulement) */}
      {type === 'extraction' && userRole === 'admin' && extractionStats && (
        <div className="extraction-admin-panel" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#2d2d2d', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ margin: 0, color: '#fff' }}><BarChart3 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Répartition par agent</h4>
            <button
              className="btn-primary"
              onClick={() => setShowReassignModal(true)}
              style={{ padding: '8px 16px' }}
            >
              <RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Réassigner des prospects
            </button>
          </div>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {extractionStats.globalStats && extractionStats.globalStats.map((stat, idx) => (
              <div key={idx} style={{ backgroundColor: '#3d3d3d', padding: '10px 15px', borderRadius: '5px', minWidth: '150px' }}>
                <div style={{ color: '#9ca3af', fontSize: '12px' }}>{stat.agent_nom || 'Non assigné'}</div>
                <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{stat.total}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de réassignation */}
      {showReassignModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3><RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Réassigner des prospects</h3>
              <button className="btn-close" onClick={() => setShowReassignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Agent source (optionnel):</label>
                <select
                  value={reassignData.source_agent_id}
                  onChange={(e) => setReassignData({...reassignData, source_agent_id: e.target.value})}
                >
                  <option value="tous">Tous les agents</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.nom}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Agent cible *:</label>
                <select
                  value={reassignData.target_agent_id}
                  onChange={(e) => setReassignData({...reassignData, target_agent_id: e.target.value})}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.nom}</option>
                  ))}
                </select>
              </div>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '10px' }}>
                Seuls les prospects avec les statuts <strong>Nouveau</strong>, <strong>NRP</strong> et <strong>Gérant pas là</strong> seront réassignés.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowReassignModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleReassign}>Réassigner</button>
            </div>
          </div>
        </div>
      )}

      {filteredProspects.length === 0 ? (
        <div className="no-prospects">
          <p>Aucun prospect trouvé</p>
          {searchTerm || filterStatut !== 'tous' ? (
            <button className="btn-reset-filters" onClick={() => {
              setSearchTerm('');
              setFilterStatut('tous');
            }}>
              Réinitialiser les filtres
            </button>
          ) : null}
        </div>
      ) : (
        <div className="prospects-table-container scrollable">
          <table className="prospects-table">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Entreprise</th>
                <th>Agent</th>
                <th>Source</th>
                <th>Date ajout</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProspects.map(prospect => (
                <tr key={prospect.id}>
                  <td className="statut-cell">
                    <button className="btn-view-inline" onClick={() => handleViewDetail(prospect)} title="Voir les détails">
                      <Eye size={14} style={{display:'inline',verticalAlign:'middle'}} />
                    </button>
                    <select
                      className={`statut-select ${getStatutBadgeClass(prospect.statut)}`}
                      value={prospect.statut}
                      onChange={(e) => handleQuickStatusChange(prospect, e)}
                      title="Cliquez pour changer le statut"
                    >
                      <option value="nouveau">🆕 Nouveau</option>
                      <option value="contacte">📞 Contacté</option>
                      <option value="interesse">👍 Intéressé</option>
                      <option value="non_interesse">👎 Pas intéressé</option>
                      <option value="faux_numero">❌ Faux numéro</option>
                      <option value="rdv_pris">📅 RDV pris</option>
                      <option value="en_negociation">💼 En négociation</option>
                      <option value="converti">✅ Converti</option>
                      {type === 'extraction' && (
                        <>
                          <option value="nrp">📵 NRP</option>
                          <option value="gerant_absent">🚫 Gérant pas là</option>
                        </>
                      )}
                    </select>
                  </td>
                  <td onClick={() => handleViewDetail(prospect)} style={{ cursor: 'pointer' }}><strong>{prospect.nom}</strong></td>
                  <td>{prospect.email || '-'}</td>
                  <td>{prospect.telephone || '-'}</td>
                  <td onClick={() => handleViewDetail(prospect)} style={{ cursor: 'pointer' }}>{prospect.entreprise || '-'}</td>
                  <td style={{ fontSize: '12px', color: prospect.agent_nom ? '#667eea' : '#999' }}>
                    {prospect.agent_nom || '-'}
                  </td>
                  <td>{prospect.source || '-'}</td>
                  <td>{new Date(prospect.created_at).toLocaleDateString('fr-FR')}</td>
                  <td className="actions-cell">
                    <button className="btn-convert" onClick={() => handleConvertToClient(prospect)} disabled={converting} title="Convertir en client">
                      <Plus size={14} style={{display:'inline',verticalAlign:'middle'}} />
                    </button>
                    <button className="btn-edit" onClick={() => handleEditProspect(prospect)} title="Modifier">
                      <Pencil size={14} style={{display:'inline',verticalAlign:'middle'}} />
                    </button>
                    {userRole === 'admin' && (
                      <button className="btn-delete" onClick={() => handleDeleteProspect(prospect.id)} title="Supprimer">
                        <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contrôles de pagination */}
      {!loading && filteredProspects.length > 0 && totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="btn-page"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft size={16} /> Début
          </button>
          <button
            className="btn-page"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <span className="page-info">
            Page {currentPage} / {totalPages}
          </span>
          <button
            className="btn-page"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Suivant <ChevronRight size={16} />
          </button>
          <button
            className="btn-page"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            Fin <ChevronsRight size={16} />
          </button>
        </div>
      )}

      {/* Modal Ajout/Modification */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingProspect ? 'Modifier le prospect' : 'Ajouter un prospect'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    nom: data.dirigeant_nom || prev.nom,
                    entreprise: data.entreprise || prev.entreprise,
                    adresse: data.adresse || prev.adresse
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Entreprise</label>
                  <input
                    type="text"
                    value={formData.entreprise}
                    onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                  >
                    <option value="nouveau">🆕 Nouveau</option>
                    <option value="contacte">📞 Contacté</option>
                    <option value="interesse">👍 Intéressé</option>
                    <option value="non_interesse">👎 Pas intéressé</option>
                    <option value="faux_numero">❌ Faux numéro</option>
                    <option value="rdv_pris">📅 RDV pris</option>
                    <option value="en_negociation">💼 En négociation</option>
                    <option value="converti">✅ Converti</option>
                    {type === 'extraction' && (
                      <>
                        <option value="nrp">📵 NRP</option>
                        <option value="gerant_absent">🚫 Gérant pas là</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="Ex: Site web, LinkedIn, Référence..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Notes sur ce prospect..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  {editingProspect ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {showDetailModal && viewingProspect && (
        <div className="modal-overlay">
          <div className="modal-content modal-detail-large">
            <div className="modal-header">
              <h3><Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Détails complets du prospect</h3>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="detail-view">
              {/* Informations principales */}
              <div className="detail-section">
                <h4><User size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Informations de contact</h4>
                <div className="detail-row">
                  <strong>Nom:</strong>
                  <span>{viewingProspect.nom}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>{viewingProspect.email || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Téléphone:</strong>
                  <span>{viewingProspect.telephone || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Entreprise:</strong>
                  <span>{viewingProspect.entreprise || '-'}</span>
                </div>
              </div>

              {/* Adresse */}
              <div className="detail-section">
                <h4><MapPin size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Localisation</h4>
                <div className="detail-row">
                  <strong>Adresse:</strong>
                  <span>{viewingProspect.adresse || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Code postal:</strong>
                  <span>{viewingProspect.code_postal || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Ville:</strong>
                  <span>{viewingProspect.ville || '-'}</span>
                </div>
              </div>

              {/* Informations légales */}
              <div className="detail-section">
                <h4><Building2 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Informations légales</h4>
                <div className="detail-row">
                  <strong>SIREN:</strong>
                  <span>{viewingProspect.siren || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>SIRET:</strong>
                  <span>{viewingProspect.siret || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Code NAF:</strong>
                  <span>{viewingProspect.naf || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Forme juridique:</strong>
                  <span>{viewingProspect.forme_juridique || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Date de création:</strong>
                  <span>{viewingProspect.date_creation || '-'}</span>
                </div>
              </div>

              {/* Informations financières */}
              <div className="detail-section">
                <h4><Briefcase size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Informations financières</h4>
                <div className="detail-row">
                  <strong>Chiffre d'affaires:</strong>
                  <span>{viewingProspect.chiffre_affaires || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Effectif:</strong>
                  <span>{viewingProspect.effectif || '-'}</span>
                </div>
              </div>

              {/* Statut et source */}
              <div className="detail-section">
                <h4><BarChart3 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Suivi</h4>
                <div className="detail-row">
                  <strong>Statut:</strong>
                  <span className={`statut-badge ${getStatutBadgeClass(viewingProspect.statut)}`}>
                    {getStatutLabel(viewingProspect.statut)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Agent assigné:</strong>
                  <span style={{ color: viewingProspect.agent_nom ? '#667eea' : '#999', fontWeight: viewingProspect.agent_nom ? '600' : 'normal' }}>
                    {viewingProspect.agent_nom || 'Non assigné'}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Source:</strong>
                  <span>{viewingProspect.source || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Date d'ajout:</strong>
                  <span>{new Date(viewingProspect.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              {/* Notes */}
              {viewingProspect.notes && (
                <div className="detail-section">
                  <h4><FileText size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Notes</h4>
                  <div className="detail-notes">
                    <p>{viewingProspect.notes}</p>
                  </div>
                </div>
              )}
            </div>
            <div style={{ margin: '16px 0' }}>
              <Encaisser source="data_pro" ficheId={viewingProspect.id}
                client={{ nom: viewingProspect.nom, entreprise: viewingProspect.entreprise, telephone: viewingProspect.telephone, email: viewingProspect.email }} />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowDetailModal(false)}>
                Fermer
              </button>
              <button className="btn-submit" onClick={() => {
                setShowDetailModal(false);
                handleConvertToClient(viewingProspect);
              }}>
                <Plus size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Convertir en client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProspectManagement;
